import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Building2, ChevronLeft, ChevronRight, Loader2, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Page, PageHeader } from '@/components/layout/PageHeader';
import { TENANT_STATUS_LABELS, TenantStatusBadge } from '@/components/tenants/TenantStatusBadge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { genericErrorMessage } from '@/lib/api-errors';
import { formatDate, formatNumber } from '@/lib/format';
import { TENANT_STATUSES, tenantKeys, tenantsApi, type TenantStatus } from '@/lib/tenants-api';

export const PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_MS = 300;

/** Filtres dans l'URL : retour arrière, F5 et lien partagé gardent la page et les filtres. */
export interface TenantsSearch {
  page?: number;
  pageSize?: number;
  status?: TenantStatus;
  search?: string;
}

export const validateTenantsSearch = (search: Record<string, unknown>): TenantsSearch => {
  const page = Number(search['page']);
  const pageSize = Number(search['pageSize']);
  const status = search['status'];
  const text = typeof search['search'] === 'string' ? search['search'].trim().slice(0, 100) : '';
  return {
    ...(Number.isInteger(page) && page > 1 && { page }),
    ...((PAGE_SIZES as readonly number[]).includes(pageSize) && pageSize !== DEFAULT_PAGE_SIZE && { pageSize }),
    ...(TENANT_STATUSES.includes(status as TenantStatus) && { status: status as TenantStatus }),
    ...(text && { search: text }),
  };
};

const COLUMNS = ['Nom', 'Slug', 'Statut', 'Pays', 'Créé le', 'Users', 'Véhicules'];

const selectClass =
  'h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export const TenantsPage = () => {
  const navigate = useNavigate({ from: '/tenants' });
  const { page = 1, pageSize = DEFAULT_PAGE_SIZE, status, search } = useSearch({ from: '/authed/tenants' });
  const [searchInput, setSearchInput] = useState(search ?? '');

  const setFilters = (patch: TenantsSearch, replace = false) =>
    void navigate({ search: (prev) => validateTenantsSearch({ ...prev, ...patch }), replace });

  // Saisie → URL après 300 ms sans frappe ; retour à la page 1 à chaque nouveau terme
  useEffect(() => {
    const value = searchInput.trim();
    if (value === (search ?? '')) return;
    const timer = setTimeout(() => setFilters({ search: value || undefined, page: undefined }, true), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // Seul le texte saisi relance le minuteur (search et navigate volontairement hors dépendances)
  }, [searchInput]);

  const params = { page, pageSize, ...(status && { status }), ...(search && { search }) };
  const query = useQuery({
    queryKey: tenantKeys.list(params),
    queryFn: () => tenantsApi.list(params),
    // Garde la page affichée pendant le chargement de la suivante (pas de clignotement)
    placeholderData: keepPreviousData,
  });

  const result = query.data;
  const filtered = Boolean(status || search);

  return (
    <Page>
      <PageHeader
        title="Tenants"
        description="Tous les tenants de la plateforme, du plus récent au plus ancien."
        actions={
          <Link to="/tenants/new" className={buttonVariants()}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Créer un tenant
          </Link>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher par nom ou slug"
            aria-label="Rechercher par nom ou slug"
            className="pl-9"
            maxLength={100}
          />
        </div>
        <select
          aria-label="Filtrer par statut"
          value={status ?? ''}
          onChange={(e) => setFilters({ status: (e.target.value || undefined) as TenantStatus | undefined, page: undefined })}
          className={selectClass}
        >
          <option value="">Tous les statuts</option>
          {TENANT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {TENANT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? (
        <div className="space-y-3">
          <FormAlert>Impossible de charger les tenants : {genericErrorMessage(query.error).toLowerCase()}</FormAlert>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                {COLUMNS.map((c, i) => (
                  <TableHead key={c} className={i >= 5 ? 'text-right' : undefined}>
                    {c}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending &&
                Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i} data-testid="tenant-row-skeleton">
                    {COLUMNS.map((c) => (
                      <TableCell key={c}>
                        <Skeleton className="h-4 w-full max-w-[8rem]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {result?.data.map((t) => (
                <TableRow
                  key={t.id}
                  data-testid="tenant-row"
                  className="cursor-pointer"
                  onClick={() => void navigate({ to: '/tenants/$id', params: { id: t.id } })}
                >
                  <TableCell className="font-medium">
                    {/* Lien réel pour le clavier, le clic milieu et les lecteurs d'écran ; la ligne entière reste cliquable */}
                    <Link to="/tenants/$id" params={{ id: t.id }} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                      {t.name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.slug}</TableCell>
                  <TableCell>
                    <TenantStatusBadge status={t.status} />
                  </TableCell>
                  <TableCell>{t.country}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(t.createdAt)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(t.usersCount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(t.vehiclesCount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {result && result.data.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
              <Building2 className="h-8 w-8 text-muted-foreground/50" aria-hidden />
              {filtered ? 'Aucun tenant ne correspond à ces filtres.' : 'Aucun tenant pour le moment.'}
            </div>
          )}
        </Card>
      )}

      {result && result.total > 0 && (
        <div className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="tenants-range">
            {formatNumber((result.page - 1) * result.pageSize + 1)}–{formatNumber((result.page - 1) * result.pageSize + result.data.length)} sur{' '}
            {formatNumber(result.total)} tenant{result.total > 1 ? 's' : ''}
            {query.isFetching && !query.isPending && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" aria-label="Chargement" />}
          </p>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              Par page
              <select
                value={pageSize}
                onChange={(e) => setFilters({ pageSize: Number(e.target.value), page: undefined })}
                className={`${selectClass} h-9`}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setFilters({ page: page - 1 })} aria-label="Page précédente">
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <span className="tabular-nums">
              Page {result.page} / {result.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= result.totalPages}
              onClick={() => setFilters({ page: page + 1 })}
              aria-label="Page suivante"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </Page>
  );
};
